import React, { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { FaTrash, FaUpload, FaImage } from 'react-icons/fa'
import adminServices from '../../services/AdminServices'
import utils from '../../utils'

function PropertyGallery({ propertyId, existingGallery = [], onImagesUpdate }) {
    const [files, setFiles] = useState([])
    const [previews, setPreviews] = useState([])
    const [selectedIds, setSelectedIds] = useState([])
    const [uploading, setUploading] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const fileInputRef = useRef(null)

    useEffect(() => {
        if (existingGallery.length >= 1) {
            setPreviews(
                existingGallery.map(img => ({
                    id: img.id,
                    url: img.url,
                    isNew: false,
                    isExisting: true
                }))
            )
        }

    }, [existingGallery])

    useEffect(() => {
        return () => {
            previews.forEach(p => {
                if (p.isNew && p.url?.startsWith('blob:')) {
                    URL.revokeObjectURL(p.url)
                }
            })
        }
    }, [previews])

    const handleFiles = (newFiles) => {
        const fileArray = Array.from(newFiles).filter(f => f.type.startsWith('image/'))
        if (fileArray.length === 0) {
            toast.error('Please select image files only')
            return
        }

        setFiles(prev => [...prev, ...fileArray])

        fileArray.forEach(file => {
            const url = URL.createObjectURL(file)
            setPreviews(prev => [...prev, {
                file,
                url,
                isNew: true,
                isExisting: false,
                id: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`
            }])
        })
    }

    const onDrop = (e) => {
        e.preventDefault()
        handleFiles(e.dataTransfer.files)
    }

    const onDragOver = (e) => e.preventDefault()

    const handleUpload = async () => {
        if (files.length === 0 || !propertyId) return
        setUploading(true)

        const formData = new FormData()
        files.forEach(file => formData.append('images', file))

        try {
            await adminServices.uploadPropertyImages(propertyId, formData)

            toast.success(`${files.length} image(s) uploaded successfully`)
            setFiles([])
            setPreviews(prev => prev.filter(p => p.isExisting))
            onImagesUpdate?.()
        } catch (err) {
            const msg = err.response?.data?.images?.[0]
                || err.response?.data?.detail
                || err.response?.data?.non_field_errors?.[0]
                || 'Upload failed. Please try again.'
            toast.error(msg)
            console.error('Upload error:', err)
        } finally {
            setUploading(false)
        }
    }

    const toggleSelect = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        )
    }

    const handleDelete = async () => {
        if (selectedIds.length === 0 || !propertyId) return
        if (!window.confirm(`Delete ${selectedIds.length} image(s)? This cannot be undone.`)) return

        setDeleting(true)
        try {
            await adminServices.deletePropertyImages(propertyId, { ids: selectedIds })

            toast.success('Image(s) deleted successfully')
            // Remove deleted items from preview list
            setPreviews(prev => prev.filter(p => !selectedIds.includes(p.id)))
            setSelectedIds([])
            onImagesUpdate?.()
        } catch (err) {
            const msg = err.response?.data?.detail
                || err.response?.data?.message
                || 'Delete failed. Please try again.'
            toast.error(msg)
            console.error('Delete error:', err)
        } finally {
            setDeleting(false)
        }
    }

    const removeNewPreview = (id) => {
        const preview = previews.find(p => p.id === id)
        if (preview?.url?.startsWith('blob:')) {
            URL.revokeObjectURL(preview.url)
        }
        setPreviews(prev => prev.filter(p => p.id !== id))
        setFiles(prev => prev.filter(f => f !== preview?.file))
    }

    const clearAllNew = () => {
        previews.forEach(p => {
            if (p.isNew && p.url?.startsWith('blob:')) {
                URL.revokeObjectURL(p.url)
            }
        })
        setFiles([])
        setPreviews(prev => prev.filter(p => p.isExisting))
        setSelectedIds([])
    }

    return (
        <div className="space-y-5">
            <div
                className="border-2 border-dashed border-outline-variant/40 rounded-xl p-8 text-center hover:border-primary/60 hover:bg-primary/5 transition cursor-pointer group"
                onDrop={onDrop}
                onDragOver={onDragOver}
                onClick={() => fileInputRef.current?.click()}
            >
                <FaImage className="mx-auto text-3xl text-on-surface-variant/40 group-hover:text-primary transition mb-3" />
                <p className="text-sm text-on-surface-variant/70">
                    Drag & drop images here, or <span className="text-primary font-semibold">click to browse</span>
                </p>
                <p className="text-xs text-on-surface-variant/40 mt-1">
                    JPG, PNG, WebP • Max 5MB each
                </p>
                <input
                    type="file"
                    multiple
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={(e) => handleFiles(e.target.files)}
                    className="hidden"
                />
            </div>

            {(files.length > 0 || selectedIds.length > 0) && (
                <div className="flex flex-wrap gap-3">
                    {files.length > 0 && (
                        <button
                            onClick={handleUpload}
                            disabled={uploading}
                            className="btn btn-primary btn-sm gap-2"
                        >
                            {uploading ? (
                                <span className="loading loading-spinner loading-xs"></span>
                            ) : <FaUpload className="text-sm" />}
                            Upload ({files.length})
                        </button>
                    )}
                    {selectedIds.length > 0 && (
                        <button
                            onClick={handleDelete}
                            disabled={deleting}
                            className="btn btn-error btn-sm gap-2"
                        >
                            {deleting ? (
                                <span className="loading loading-spinner loading-xs"></span>
                            ) : <FaTrash className="text-sm" />}
                            Delete ({selectedIds.length})
                        </button>
                    )}
                    {files.length > 0 && (
                        <button
                            onClick={clearAllNew}
                            className="btn btn-ghost btn-sm"
                        >
                            Clear All
                        </button>
                    )}
                </div>
            )}

            {previews.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {previews.slice().reverse().map((item) => (
                        <div key={item.id} className="group relative aspect-square">
                            {item.isExisting && (
                                <input
                                    type="checkbox"
                                    checked={selectedIds.includes(item.id)}
                                    onChange={() => toggleSelect(item.id)}
                                    className="absolute top-2 right-2 z-10 w-4 h-4 rounded border-outline-variant bg-white checked:bg-primary checked:border-primary cursor-pointer"
                                />
                            )}

                            <img
                                src={!item.isExisting ? item.url : utils.baseUrl + item.url}
                                alt="Gallery preview"
                                className="w-full h-full object-cover rounded-lg border border-outline-variant/20 group-hover:border-primary/40 transition"
                                loading="lazy"
                            />
                            {item.isNew && (
                                <span className="absolute bottom-2 left-2 bg-primary/90 text-white text-[10px] px-2 py-0.5 rounded-full font-medium shadow-sm">
                                    New
                                </span>
                            )}

                            {item.isNew && (
                                <button
                                    type="button"
                                    onClick={() => removeNewPreview(item.id)}
                                    className="absolute top-2 right-2 z-10 w-6 h-6 rounded-full bg-error/90 text-white flex items-center justify-center hover:bg-error transition shadow-sm"
                                    title="Remove"
                                >
                                    <FaTrash className="text-[10px]" />
                                </button>
                            )}
                            {selectedIds.includes(item.id) && (
                                <div className="absolute inset-0 bg-primary/10 rounded-lg pointer-events-none" />
                            )}
                        </div>
                    ))}
                </div>
            )}
            {!previews.length && (
                <div className="text-center py-8 text-on-surface-variant/50 text-sm">
                    No images yet. Upload some to showcase this property.
                </div>
            )}
        </div>
    )
}

export default PropertyGallery